import {
  map,
  mapValues,
  pick,
  pickBy,
  range,
  tail,
  toPairs,
} from "lodash";
import moment from "moment-timezone";
import { fields } from "./fields";
import headers from "./headers";
import { mappedBy } from "./query";
import { Payload, Record } from "./types";

const separator = (value: string) => {
  switch (value.trim().slice(-1)) {
    case ".":
    case ",":
      return " ";
    default:
      return ", ";
  }
};

// appendNote returns a new Record with the given note appended to the existing
// notes field, if it has been mapped to.
const appendNote = (payload: Payload<Record>, record: Record, note: string): Record => {
  const key = mappedBy(payload, "notes");
  if (!key) {
    return record;
  }

  const notes = record[key];
  if (notes === "") {
    return { ...record, [key]: note };
  }

  return { ...record, [key]: `${notes}${separator(notes)}${note}` };
};

// serializeValue returns a string with the given key and value serialized for
// reliable querying. One usage of this would be to store serialized values
// into the notes field in order to reliably query it later when we are able
// to support it (like multiple values).
const serializeValue = (key: string, value: string): string => `{{${key}:${value}}}`;

// update returns a new Payload<R> from the given Payload<T>, ensuring the
// returned value is a shallow copy of the original.
const updateData = <T, R>(payload: Payload<T>, fn: (data: T[]) => R[]): Payload<R> =>
  ({ ...payload, data: fn(payload.data) });

// removeEmptyRecords returns a new Payload<string[]> containing only records
// which have at least one populated value. This is useful to strip empty rows
// from the end of the CSV that Excel sometimes puts there.
export const removeEmptyRecords: Identity<Payload<string[]>> = payload =>
  updateData(payload, data => data.filter(record => record.some(Boolean)));

// removeEmptyColumns returns a transform function which removes all empty
// columns. The first argument should be true if the first row is known to
// contain the headers.
export const removeEmptyColumns = (firstRowIsHeaders: boolean) =>
  (payload: Payload<string[]>): ReturnType<typeof updateData> => {
    const data = firstRowIsHeaders ? tail(payload.data) : payload.data;
    const empty = range(0, data[0].length).reduce(
      (indices, index) =>
        (map(data, index).some(Boolean) ? indices : { ...indices, [index]: true }),
      {},
    );

    return updateData(payload, dataToUpdate =>
      dataToUpdate.map(record => record.filter((_, index) => !(index in empty))));
  };

// keyedValues returns a new Payload<Record> from the given Payload<string[]>,
// converting arrays of strings (["foo", "bar", "baz"]) to objects of key/value
// pairs {a: "foo", b: "bar", c: "baz"} and removing the first row which is
// assumed to contain the headers. This (hopefully) makes it easier to query
// the given record.
export const keyedValues = (payload: Payload<string[]>): Payload<Record> => {
  const [rowHeaders] = payload.data;

  return updateData(payload, data =>
    tail(data).map(records =>
      records.reduce((result, value, index) => ({ ...result, [rowHeaders[index]]: value }), {})));
};

export const trimData = (payload: Payload<string[]>): ReturnType<typeof updateData> =>
  updateData(payload, data => data.map(row => row.map(column => column.trim())));

const pad2 = (x: number): string => x.toString().padStart(2, "0");

const parse2DigitYear = (yearStr: string, floor: number): string => {
  const year = parseInt(yearStr, 10);
  return yearStr.length > 2 ? yearStr : (year < floor ? `20${pad2(year)}` : `19${pad2(year)}`);
};

export const parseTime = (time: string): {valid: boolean, result: string} => {
  const regex = /(?<hour>\d{1,2}):(?<min>\d{1,2})(:(?<sec>\d{1,2}))?\s*(?<amPm>[AP]M)?/i;
  const match = time.match(regex);

  if (match?.groups) {
    let { hour, min, sec = "00", amPm = "" } = match.groups;
    const hourNum = parseInt(hour, 10);
    const minNum = parseInt(min, 10);
    const secNum = parseInt(sec, 10);

    // Validate the hour, minute, and second ranges based on AM/PM notation
    if ((hourNum < 1 || hourNum > 12) || minNum > 59 || secNum > 59) {
      return { valid: false, result: "Invalid time format" };
    }

    // Normalize the hour to handle the 12 AM and 12 PM cases
    if (hourNum === 12 && amPm.toUpperCase() === "AM") {
      hour = "00"; // Midnight case
    } else if (hourNum === 12 && amPm.toUpperCase() === "PM") {
      hour = "12"; // Noon case
    } else {
      hour = pad2(hourNum); // Normal AM/PM hours
    }

    // Ensure AM/PM is in uppercase for standardization
    amPm = amPm.toUpperCase();

    return { valid: true, result: `${hour}:${pad2(minNum)}:${pad2(secNum)} ${amPm}` };
  }

  return { valid: false, result: "Invalid time format" };
};

export const parseDate = (date: string): {valid: boolean, result: string} => {
  const regex = /^(?<month>\d{1,2})[/-](?<day>\d{1,2})[/-](?<year>\d\d\d?\d?)$/;
  const match = date.match(regex);

  if (match?.groups) {
    const { day, month, year } = match.groups;
    const parsedYear = parse2DigitYear(year, 40);
    const paddedDay = pad2(parseInt(day));
    const paddedMonth = pad2(parseInt(month));

    // Create a date object to validate month and day
    const testDate = new Date(parseInt(parsedYear), parseInt(month) - 1, parseInt(day));
    if (testDate.getFullYear() !== parseInt(parsedYear) ||
        testDate.getMonth() !== parseInt(month) - 1 ||
        testDate.getDate() !== parseInt(day)) {
      return { valid: false, result: "Invalid date format" };
    }

    return { valid: true, result: `${paddedMonth}/${paddedDay}/${parsedYear}` };
  }
  return { valid: false, result: "Invalid date format" };
};


export const parseDateTime = (date?: string | null, time?: string | null): {valid: boolean, result: string} => {
  if (!date) return { valid: false, result: "Date Missing" };
  if (!time) return parseDate(date);

  const { valid: dateValid, result: parsedDate } = parseDate(date);
  const { valid: timeValid, result: parsedTime } = parseTime(time);
  if (!dateValid) return { valid: dateValid, result: parsedDate };
  if (!timeValid) return { valid: timeValid, result: parsedTime };
  return { valid: true, result: `${parsedDate} ${parsedTime}` };
};


// adjustDatetimes returns a new Payload<Record> whose values mapped to "date"
// (Submit date) and "time" (Submit time) are parsed within the given timezone
// and then converted to UTC for uniformity.
export const adjustDatetimes: Identity<Payload<Record>> = payload => updateData(payload, data => {
  const datename = mappedBy(payload, "date");
  const timename = mappedBy(payload, "time");
  if (!datename || !timename) return data;

  return data.map(record => {
    const datevalue = record[datename];
    const timevalue = record[timename];

    if (!timevalue) return record;

    const combinedDateTime = parseDateTime(datevalue, timevalue);

    if (!combinedDateTime.valid) return record;

    const date = moment.tz(
      combinedDateTime.result,
      "MM/DD/YYYY hh:mm:ss A",
      payload.timezone,
    );

    if (!date.isValid()) return record;

    return mapValues(record, (value, key) => {
      switch (key) {
        case datename:
          return date.utc().format("MM/DD/YYYY");
        case timename:
          return date.utc().format("hh:mm:ss A");
        default:
          return value;
      }
    });
  });
});

// appendNotes returns a new Payload<Record> whose values mapped to "add_notes"
// (Append to notes) are appended to the notes column. If no notes column
// exists, create one and ensure that it is mapped to the "notes" (Notes)
// field.
export const appendNotes: Identity<Payload<Record>> = payload => {
  const { mapping } = payload;

  if (!Object.values(mapping).some(({ field }) => field === "add_notes")) {
    return payload;
  }

  const result = updateData(payload, data => {
    const column = mappedBy(payload, "notes") || "notes";

    const [candidate] = data;
    if (!candidate) {
      return data;
    }

    const appending = Object.keys(
      pickBy(candidate, (_, key) =>
        (key in mapping ? fields[mapping[key].field].format === "append_note" : false)),
    );

    return data.map(record => {
      const initial = record[column] ? [record[column]] : [];
      const note = Object.entries(pick(record, appending)).reduce(
        (memo, [key, value]) => (value ? memo.concat(`${mapping[key].format} ${value}`) : memo),
        initial,
      );

      return { ...record, [column]: note.join(", ") };
    });
  });

  return {
    ...result,
    mapping: {
      ...result.mapping,
      notes: { field: "notes", format: "" },
    },
  };
};

// persistUnsupportedValues returns a new Payload<Record> whose currently
// unsupported values are moved to the notes for later retrieval.
export const persistUnsupportedValues: Identity<Payload<Record>> = payload =>
  updateData(payload, data =>
    data.map(record => {
      const key = mappedBy(payload, "household_ami");
      if (!key) {
        return record;
      }

      // When household_ami contains a forward slash, assume the value is in
      // the format of eg. 50/60/70 and save it to notes instead and set the
      // column to an empty string.
      const val = record[key];
      if (val.indexOf("/") === -1) {
        return record;
      }

      const serialized = serializeValue("household_ami", val);
      const appended = appendNote(payload, record, serialized);
      return { ...appended, [key]: "" };
    }));

export const castValues: Identity<Payload<{ [x: string]: string | undefined; }>> = payload =>
  updateData(payload, data =>
    data.map(record =>
      mapValues(record, (value, key) => {
        if (!(key in payload.mapping)) {
          return value;
        }

        const mapped = payload.mapping[key];
        if (!(mapped.field in fields)) {
          return value;
        }

        const { schema } = fields[mapped.field];
        if (!schema) {
          return value;
        }

        try {
          return schema.cast(value);
        } catch {
          return value;
        }
      })));

// conformStructure returns a new Payload<string[]> from the given
// Payload<Record> that is ready to be marshalled as a CSV string and exported
// to the user. This ensures that the CSV contains the column names and
// structure we expect to import into the system.
export const conformStructure = (payload: Payload<Record>): Payload<string[]> =>
  updateData(payload, data => {
    const headersWithPassThroughHeaders = headers.concat(
      toPairs(payload.mapping)
        .filter(([, val]) => val.field === "passthrough")
        .map(([key]) => key),
    );

    return [headersWithPassThroughHeaders].concat(
      data.map(record =>
        headersWithPassThroughHeaders.map(header => {
          // passthrough fields need to use header directly as we
          // can't map to a known header using mappedBy
          if (payload.mapping[header]?.field === "passthrough") {
            return record[header] || "";
          }

          const key = mappedBy(payload, header);
          return key ? record[key] : "";
        }, [])),
    );
  });
